import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, WindowLocation, Location } from '@reach/router';

interface ListenerData {
  location: WindowLocation;
}

interface HandleReloadOptions {
  boundaryBreak?: boolean;
  firstLoad?: boolean;
}

export interface NavigateListenerProps {
  targetWindow?: Window;
  onLoad?: (data: ListenerData) => Promise<void> | void;
  onUnload?: (data: ListenerData) => Promise<void> | void;
}

export const NavigateListener: React.FC<NavigateListenerProps> = props => {
  const { onLoad, onUnload, targetWindow } = props;

  const location = useLocation();
  const [lastLocation, setLastLocation] = useState(location);

  const handleUnloadReload = useCallback(
    (options: HandleReloadOptions = {}) => {
      const { firstLoad = false, boundaryBreak = false } = options;
      try {
        // If this isn't the first load (direct hit of endpoint) and there
        // is a listener, we have an old page to unload
        if (!firstLoad && onUnload) {
          onUnload({ location: lastLocation });
        }

        // If we are landing on a non-boundary break and have a listener,
        // call it with the new location we're expecting
        if (!boundaryBreak && onLoad) {
          onLoad({ location });
        }
      } finally {
        // Update the location no matter what
        setLastLocation(location);
      }
    },
    [onLoad, onUnload, lastLocation, location]
  );

  // Register for reach-router based navigation
  useEffect(() => {
    const { pathname: oldPathname } = lastLocation;

    if (oldPathname !== location.pathname) {
      handleUnloadReload();
    }
  });

  // Register for navigation across boundaries (page load)
  useEffect(() => {
    if (targetWindow) {
      const boundaryEvent = 'unload';
      const cb = () => handleUnloadReload({ boundaryBreak: true });
      targetWindow.addEventListener(boundaryEvent, cb);
      return () => targetWindow.removeEventListener(boundaryEvent, cb);
    }
  });

  // This is the first time this is running, we should treat it like a load
  useEffect(
    () => handleUnloadReload({ firstLoad: true }),
    // Needs to be investigated by: https://jira.cengage.com/browse/IAM-1219
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return <></>;
};

/**
 * This ugly piece of code is necessary to be able to reliably listen
 * to each of the events that occurs to the location. As of now, it's not
 * possible to mark a child of the Router as just a listener, they must
 * each have a path and exclusively render.
 *
 * This gets around that by rendering the NavigateListener within the
 * Location context, and then renders the other children.
 *
 * Note that the location provided to this child function is not actually
 * used, instead, the context is used by the NavigateListener.
 */
export const NavigateListenerWrap: React.FC<NavigateListenerProps> = props => {
  const { children, ...rest } = props;
  return (
    <Location>
      {() => {
        return (
          <>
            <NavigateListener {...rest} />
            {children}
          </>
        );
      }}
    </Location>
  );
};
